import { useState } from "react";
// Components
import { Grid, Theme } from "@mui/material"; //Typography
import { useTheme } from "@mui/styles";
import { IAirport } from "@vmosolutions/oc-interfaces";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { AirportInfo } from "./AirportInfo";
// API Calls
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { airportSearchString, findAirports } from "src/utils/airportHelper";

interface IAirportInfoWindowProps {
  id: string;
  path: string[];
  windowProps: {
    airportInfo: IAirport;
  };
}

const airportinfoWindowStyles = (theme: Theme): any => ({
  root: {
    backgroundColor: theme.custom.timeline.contextListBackground,
    width: "100%",

    "& .window_title": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      "& .page_header": {
        margin: "5px 0px",
        "& .subheader": {
          margin: "0px 10px",
          color: theme.custom.forms.labels,
        },
      },
    },

    "& .airport_search": { minWidth: "300px", margin: "10px" },

    "& .airport_info": {
      padding: "5px",
      minWidth: "210px",
      marginRight: "10px",
    },

    "& .loading_spinner": {
      marginTop: "-16px",
      marginLeft: "-16px",
    },
  },
});

/**
 * Display AirportInfo inside Window
 */
export const AirportInfoWindow = (props: IAirportInfoWindowProps): JSX.Element => {
  const { id, path, windowProps } = props;
  const { airportInfo } = windowProps;

  ///////////////////////////////
  // MANAGE CUSTOM AIRPORTS LIST
  ///////////////////////////////
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [airports, setAirports] = useState<IAirport[]>([]);
  const [selectedAirport, setSelectedAirport] = useState<IAirport>(airportInfo ?? null);

  const addAirportsToList = async (searchString: string): Promise<void> => {
    if (searchString?.length === 4) {
      const matchFound = airports.find((ap) => ap.Ident.toUpperCase() === searchString.toUpperCase());
      if (!matchFound) {
        setIsLoading(true);
        const response: IAirport[] = await findAirports(searchString);
        if (Array.isArray(response)) {
          setAirports([...airports, ...response]); // Add to Airports List.
        }
        setIsLoading(false);
      }
    }
  };

  return (
    <Grid
      container
      sx={(theme) => airportinfoWindowStyles(theme).root}
      direction="column"
      wrap="nowrap"
      alignItems="flex-start"
    >
      <SpinnerProgress className="loading_spinner" loading={isLoading} message="Loading Airport Info" />

      <Grid item className="airport_search">
        <SelectField
          className="airportgroup"
          name="airport"
          label=""
          isMulti={false}
          onInputChange={(search: any) => {
            addAirportsToList(search);
          }}
          options={airports}
          getOptionLabel={(airport: IAirport) => `${airport.Ident} - ${airport.Name}`}
          getOptionValue={(airport: IAirport) => airport._id}
          value={selectedAirport}
          onChange={setSelectedAirport}
          isSearchable={true}
          menuPosition={"fixed"}
          openMenuOnFocus={false}
          isClearable={true}
          placeholder="Search Airport ICAO/Name"
          noOptionsMessage={() => airportSearchString}
        />
      </Grid>

      <Grid item>
        <AirportInfo id={id} path={path} airportInfo={selectedAirport} />
      </Grid>
    </Grid>
  );
};
